<template>
  <v-dialog
    :value="isEncaminharTicketDialogOpen"
    max-width="600px"
    persistent
  >
    <v-card class="px-4 py-6">
      <btn-close-dialog :handle-click="closeDialog"></btn-close-dialog>

      <v-card-title class="justify-center text-h5 pt-1">
        Encaminhar ticket
      </v-card-title>

      <v-card-text class="mt-5">
        <v-form
          ref="formRef"
          class="multi-col-validation"
          @submit.prevent="handleSubmit"
        >
          <v-row>
            <v-col cols="12">
              <v-autocomplete
                v-model="filaSelecionadaId"
                dense
                outlined
                clearable
                open-on-clear
                validate-on-blur
                label="Nova fila"
                item-text="nome"
                item-value="id"
                no-data-text="Nenhuma fila disponível"
                :loading="isGetFilasLoading"
                :items="filas"
                :rules="[validators.select]"
                @click="getFilas"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-autocomplete
                v-model="agenteSelecionadoId"
                dense
                outlined
                clearable
                open-on-clear
                validate-on-blur
                label="Novo agente"
                item-text="agenteNome"
                item-value="agenteId"
                no-data-text="Nenhum agente disponível"
                :loading="isGetAgentesLoading"
                :items="agentesDaFila"
              />
            </v-col>
          </v-row>

          <v-row class="mt-5">
            <v-col
              cols="12"
              class="d-flex justify-center"
            >
              <v-btn
                class="mr-4"
                outlined
                color="secondary"
                @click="resetFormAndCloseDialog"
              >
                Cancelar
              </v-btn>

              <v-btn
                color="primary"
                type="submit"
                :disabled="!ticketId"
              >
                <v-progress-circular
                  v-if="loading"
                  class="mx-3"
                  indeterminate
                  color="secundary"
                />
                <span v-else>Encaminhar</span>
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import Vue from 'vue'
import { required, select } from '@core/utils/validation'
import { ref, watch } from '@vue/composition-api'
import BtnCloseDialog from '@/components/btn-close-dialog/BtnCloseDialog.vue'
import TicketService from '@/services/modules/TicketService'
import useDialog from '@/composables/useDialog'
import FilaService from '@/services/modules/FilaService'
import { EventBus } from '@/utils/EventBus'

export default {
  emits: ['update:is-encaminhar-ticket-dialog-open', 'refetch-data'],

  components: {
    BtnCloseDialog,
  },

  props: {
    isEncaminharTicketDialogOpen: {
      type: Boolean,
      required: true,
    },
    ticketId: {
      type: Number,
      required: true,
    },
  },

  setup(props, { emit }) {
    const filaSelecionadaId = ref(null)
    const agenteSelecionadoId = ref(null)

    const filas = ref([])
    const isGetFilasLoading = ref(false)

    async function getFilas() {
      if (isGetFilasLoading.value || filas.value.length > 0) return
      try {
        isGetFilasLoading.value = true
        filas.value = await FilaService.getFilasSemPaginacaoEContadoresSla()
      } finally {
        isGetFilasLoading.value = false
      }
    }

    const agentesDaFila = ref([])
    const isGetAgentesLoading = ref(false)

    async function getAgentesDaFila() {
      if (!filaSelecionadaId.value) return
      try {
        isGetAgentesLoading.value = true
        agentesDaFila.value = await FilaService.getAgentesFila(filaSelecionadaId.value)
      } catch {
        agentesDaFila.value = []
        Vue.$toast.error('Não foi possível carregar os agentes desta fila.')
      } finally {
        isGetAgentesLoading.value = false
      }
    }

    function resetAgenteData() {
      agentesDaFila.value = []
      agenteSelecionadoId.value = null
    }

    async function handleFilaIdChange() {
      if (isGetAgentesLoading.value) return
      resetAgenteData()
      await getAgentesDaFila()
    }

    watch(filaSelecionadaId, v => {
      if (v) {
        handleFilaIdChange()
      } else {
        resetAgenteData()
      }
    })

    const { formRef, loading, resetForm, closeDialog } = useDialog(emit, 'is-encaminhar-ticket-dialog-open')

    function resetFormAndCloseDialog() {
      resetForm()
      closeDialog()
    }

    async function encaminharTicket() {
      try {
        loading.value = true
        await TicketService.encaminharTicket(props.ticketId, filaSelecionadaId.value, agenteSelecionadoId.value)
        emit('refetch-data')
        EventBus.$emit('refetch-ticket-slas')
        setTimeout(() => { loading.value = false }, 1000)
        resetFormAndCloseDialog()
      } catch {
        loading.value = false
      }
    }
    async function handleSubmit() {
      if (loading.value) return
      if (formRef.value.validate()) {
        await encaminharTicket()
      } else {
        Vue.$toast.warning('Preencha corretamente os campos antes de encaminhar')
      }
    }

    return {
      filaSelecionadaId,
      agenteSelecionadoId,
      filas,
      isGetFilasLoading,
      getFilas,
      agentesDaFila,
      isGetAgentesLoading,
      getAgentesDaFila,
      formRef,
      loading,
      closeDialog,
      resetFormAndCloseDialog,
      handleSubmit,
      validators: {
        required,
        select,
      },
    }
  },
}
</script>
